import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { loadUsers } from "../User/usersData";
import {
  loadCardStatements,
  loadCreditCards,
  saveCardStatements,
  saveCreditCards,
  saveGeneratedCardClaim,
} from "./creditCardsData";
import "./credit-cards.css";

const money = (value) =>
  `RM ${Number(value || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const statusClass = (status = "") => status.toLowerCase().replaceAll(" ", "-");
const selectAmount = (event) => event.currentTarget.select();

const CreditCardsPage = ({ view = "setup" }) => {
  const navigate = useNavigate();
  const isStatementView = view === "statements";
  const users = useMemo(() => loadUsers(), []);
  const [cards, setCards] = useState(loadCreditCards);
  const [statements, setStatements] = useState(loadCardStatements);
  const [editingCard, setEditingCard] = useState(null);
  const [activeStatement, setActiveStatement] = useState(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberPickerOpen, setMemberPickerOpen] = useState(false);
  const userOptions = useMemo(
    () => users.map((user) => ({ value: user.name, label: user.name })),
    [users],
  );

  const cardById = (id) => cards.find((card) => card.id === id);
  const updateCards = (next) => {
    setCards(next);
    saveCreditCards(next);
  };
  const updateStatements = (next) => {
    setStatements(next);
    saveCardStatements(next);
  };
  const total = (statement) =>
    statement.items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const difference = (statement) =>
    Number(statement.statementAmount || 0) - total(statement);

  const saveCard = (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const value = {
      ...editingCard,
      id: editingCard.id || `CARD-${String(cards.length + 1).padStart(3, "0")}`,
      bank: form.get("bank"),
      label: form.get("label"),
      last4: form.get("last4"),
      owner: editingCard.owner,
      pic: editingCard.pic,
      approver: editingCard.approver,
      statementDay: Number(form.get("statementDay")),
      members: editingCard.members || [],
    };
    updateCards(
      editingCard.id
        ? cards.map((card) => (card.id === value.id ? value : card))
        : [...cards, value],
    );
    setEditingCard(null);
    setMemberSearch("");
    setMemberPickerOpen(false);
  };

  const availableMembers = users.filter(
    (user) =>
      !editingCard?.members?.includes(user.name) &&
      user.name.toLowerCase().includes(memberSearch.trim().toLowerCase()),
  );

  const addCardMember = (name) => {
    setEditingCard({
      ...editingCard,
      members: [...(editingCard.members || []), name],
    });
    setMemberSearch("");
    setMemberPickerOpen(false);
  };

  const removeCardMember = (name) => {
    setEditingCard({
      ...editingCard,
      members: editingCard.members.filter((member) => member !== name),
    });
  };

  const saveStatement = () => {
    const next = statements.map((item) =>
      item.id === activeStatement.id ? activeStatement : item,
    );
    updateStatements(next);
    setActiveStatement(null);
  };

  const changeStatus = (status, notification) => {
    const value = {
      ...activeStatement,
      status,
      notifications: notification
        ? [...(activeStatement.notifications || []), notification]
        : activeStatement.notifications || [],
    };
    setActiveStatement(value);
    updateStatements(
      statements.map((item) => (item.id === value.id ? value : item)),
    );
  };

  const createClaimFromStatement = () => {
    const card = cardById(activeStatement.cardId);
    const claimId = activeStatement.claimId || `CL-2026-CC-${String(Date.now()).slice(-4)}`;
    const value = {
      ...activeStatement,
      claimId,
      status: "Submitted",
      notifications: [
        ...(activeStatement.notifications || []),
        "Submitted for claim",
        "Claim created",
      ],
    };
    saveGeneratedCardClaim({
      id: claimId,
      type: "Credit Card Claim",
      title: `${card?.bank || "Corporate card"} monthly statement`,
      desc: `${activeStatement.period} statement •••• ${card?.last4 || ""}`,
      period: activeStatement.period,
      code: "—",
      amount: total(activeStatement).toLocaleString("en-MY", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      status: "Pending Approval",
      sourceStatementId: activeStatement.id,
    });
    setActiveStatement(value);
    updateStatements(
      statements.map((item) => (item.id === value.id ? value : item)),
    );
    setActiveStatement(null);
    navigate("/claim");
  };

  const submitStatementAmount = () => {
    const hasAmount = Number(activeStatement.statementAmount) > 0;
    changeStatus(
      hasAmount ? "Pending Filling" : "Pending Submission",
      hasAmount ? "Card members notified" : "Card owner notified: zero statement",
    );
  };

  const closeStatement = (notification) =>
    changeStatus("Closed", notification);

  const workflowStep = (status) =>
    ({ "Pending Amount": 1, "Pending Filling": 2, "Pending Submission": 3, Submitted: 4, Closed: 5 })[status] || 1;
  const workflowLabels = ["Amount", "Filling", "Submission", "Submitted", "Closed"];

  return (
    <main className="credit-cards-page">
      <header className="credit-cards-page-heading">
        <div className="credit-cards-title-group">
          <span className="credit-cards-title-icon">
            <i className="ri-bank-card-line" />
          </span>
          <div>
            <h1>{isStatementView ? "Credit Card Statement" : "Credit Cards"}</h1>
            <p>
              {isStatementView
                ? "Review automatically generated monthly card statements and reconcile them before they become claims."
                : "Manage corporate cards, owners, persons in charge, approvers, members and statement dates."}
            </p>
          </div>
        </div>
        <button
          className="credit-cards-primary"
          type="button"
          onClick={() => setEditingCard({ members: [], statementDay: 25 })}
          hidden={isStatementView}
        >
          <i className="ri-add-line" />{" "}
          Add Credit Card
        </button>
      </header>

      {isStatementView && <section className="credit-cards-metrics">
        <div>
          <span>Active Cards</span>
          <strong>{cards.length}</strong>
        </div>
        <div>
          <span>Pending Amount</span>
          <strong>
            {statements.filter((item) => item.status === "Pending Amount").length}
          </strong>
        </div>
        <div>
          <span>Pending Filling</span>
          <strong>
            {
              statements.filter((item) => item.status === "Pending Filling")
                .length
            }
          </strong>
        </div>
        <div>
          <span>Pending Submission</span>
          <strong>
            {statements.filter((item) => item.status === "Pending Submission").length}
          </strong>
        </div>
      </section>}

      <section className="credit-cards-workspace">
        {isStatementView && (
          <div className="credit-cards-auto-note">
            <i className="ri-refresh-line" />
            <div>
              <strong>Statements are created automatically</strong>
              <span>Each card generates a monthly statement on its configured statement day, then notifies the assigned PIC.</span>
            </div>
          </div>
        )}
        {!isStatementView ? (
          <div className="credit-cards-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Credit Card</th>
                  <th>Owner</th>
                  <th>Person in Charge</th>
                  <th>Statement Date</th>
                  <th>Members</th>
                  <th>Approval Person</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cards.map((card) => (
                  <tr key={card.id}>
                    <td>
                      <strong>
                        {card.bank} {card.label}
                      </strong>
                      <small>•••• {card.last4}</small>
                    </td>
                    <td>{card.owner}</td>
                    <td>{card.pic}</td>
                    <td>{card.statementDay} of each month</td>
                    <td>
                      <button
                        className="credit-cards-link"
                        onClick={() => setEditingCard(card)}
                      >
                        {card.members.length} members
                      </button>
                    </td>
                    <td>{card.approver}</td>
                    <td>
                      <button
                        className="credit-cards-icon-button"
                        onClick={() => setEditingCard(card)}
                        aria-label="Edit card"
                      >
                        <i className="ri-edit-line" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="credit-cards-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Statement</th>
                  <th>Card</th>
                  <th>Statement Amount</th>
                  <th>Items Total</th>
                  <th>Difference</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {statements.map((statement) => {
                  const card = cardById(statement.cardId);
                  const diff = difference(statement);
                  return (
                    <tr key={statement.id}>
                      <td>
                        <strong>{statement.period}</strong>
                        <small>{statement.id}</small>
                      </td>
                      <td>
                        {card?.bank} •••• {card?.last4}
                        <small>PIC: {card?.pic}</small>
                      </td>
                      <td>{money(statement.statementAmount)}</td>
                      <td>
                        {money(total(statement))}
                        <small>{statement.items.length} items</small>
                      </td>
                      <td
                        className={
                          diff === 0
                            ? "credit-cards-match"
                            : "credit-cards-mismatch"
                        }
                      >
                        {diff === 0 ? "Matched" : money(diff)}
                      </td>
                      <td>
                        <div
                          className="credit-cards-progress"
                          aria-label={`Statement progress: ${statement.status}`}
                        >
                          {workflowLabels.map((label, index) => {
                            const step = index + 1;
                            const currentStep = workflowStep(statement.status);
                            const isDone = currentStep > step;
                            const isActive = currentStep === step;

                            return (
                              <div
                                key={label}
                                className={`credit-cards-progress-step${isDone ? " done" : ""}${isActive ? " active" : ""}`}
                                aria-current={isActive ? "step" : undefined}
                              >
                                <span className="credit-cards-progress-marker">
                                  {isDone ? (
                                    <i
                                      className="ri-check-line"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    step
                                  )}
                                </span>
                                <span className="credit-cards-progress-label">
                                  {label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`credit-cards-status ${statusClass(statement.status)}`}
                        >
                          {statement.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="credit-cards-secondary"
                          onClick={() =>
                            setActiveStatement({
                              ...statement,
                              items: statement.items.map((item) => ({
                                ...item,
                              })),
                            })
                          }
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editingCard &&
        createPortal(
          <div className="credit-cards-modal-backdrop">
            <form className="credit-cards-modal" onSubmit={saveCard}>
              <div className="credit-cards-modal-head">
                <h2>
                  {editingCard.id ? "Edit Credit Card" : "Add Credit Card"}
                </h2>
                <button type="button" onClick={() => setEditingCard(null)}>
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="credit-cards-form-grid">
                <label>
                  Bank
                  <input
                    name="bank"
                    required
                    defaultValue={editingCard.bank}
                    placeholder="e.g. Maybank"
                  />
                </label>
                <label>
                  Card Name
                  <input
                    name="label"
                    required
                    defaultValue={editingCard.label}
                    placeholder="e.g. Corporate Platinum"
                  />
                </label>
                <label>
                  Last 4 Digits
                  <input
                    name="last4"
                    required
                    maxLength="4"
                    pattern="[0-9]{4}"
                    defaultValue={editingCard.last4}
                  />
                </label>
                <label>
                  Statement Day
                  <input
                    name="statementDay"
                    required
                    type="number"
                    min="1"
                    max="31"
                    defaultValue={editingCard.statementDay}
                  />
                </label>
                {[
                  ["owner", "Owner", "Select owner"],
                  ["pic", "Person in Charge", "Select PIC"],
                  ["approver", "Approval Person", "Select approver"],
                ].map(([field, label, placeholder]) => (
                  <div
                    className={`credit-cards-search-select-field ${
                      field === "approver" ? "full" : ""
                    }`}
                    key={field}
                  >
                    <label htmlFor={`credit-card-${field}`}>{label}</label>
                    <Select
                      inputId={`credit-card-${field}`}
                      classNamePrefix="credit-cards-search-select"
                      options={userOptions}
                      value={
                        userOptions.find(
                          (option) => option.value === editingCard[field],
                        ) || null
                      }
                      onChange={(option) =>
                        setEditingCard({
                          ...editingCard,
                          [field]: option?.value || "",
                        })
                      }
                      placeholder={placeholder}
                      noOptionsMessage={() => "No matching users"}
                      isSearchable
                      required
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 10000 }),
                      }}
                    />
                  </div>
                ))}
                <div className="full credit-cards-member-field">
                  <label htmlFor="credit-card-member-search">
                    Card Members
                  </label>
                  <div className="credit-cards-member-picker">
                    <i className="ri-search-line" />
                    <input
                      id="credit-card-member-search"
                      type="search"
                      value={memberSearch}
                      placeholder="Search and add a member"
                      autoComplete="off"
                      onFocus={() => setMemberPickerOpen(true)}
                      onChange={(event) => {
                        setMemberSearch(event.target.value);
                        setMemberPickerOpen(true);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && availableMembers[0]) {
                          event.preventDefault();
                          addCardMember(availableMembers[0].name);
                        }
                        if (event.key === "Escape") {
                          setMemberPickerOpen(false);
                        }
                      }}
                    />
                    {memberPickerOpen && (
                      <div className="credit-cards-member-options">
                        {availableMembers.length > 0 ? (
                          availableMembers.slice(0, 8).map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => addCardMember(user.name)}
                            >
                              <span className="credit-cards-member-avatar">
                                {user.name
                                  .split(" ")
                                  .map((part) => part[0])
                                  .join("")
                                  .slice(0, 2)}
                              </span>
                              <span>{user.name}</span>
                              <i className="ri-add-line" />
                            </button>
                          ))
                        ) : (
                          <p>No matching members available.</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="credit-cards-selected-members">
                    {(editingCard.members || []).map((member) => (
                      <span key={member}>
                        {member}
                        <button
                          type="button"
                          onClick={() => removeCardMember(member)}
                          aria-label={`Remove ${member}`}
                        >
                          <i className="ri-close-line" />
                        </button>
                      </span>
                    ))}
                    {editingCard.members?.length === 0 && (
                      <small>No card members added yet.</small>
                    )}
                  </div>
                </div>
              </div>
              <div className="credit-cards-modal-actions">
                <button
                  type="button"
                  className="credit-cards-secondary"
                  onClick={() => setEditingCard(null)}
                >
                  Cancel
                </button>
                <button className="credit-cards-primary">Save Card</button>
              </div>
            </form>
          </div>,
          document.body,
        )}

      {activeStatement &&
        createPortal(
          <div className="credit-cards-modal-backdrop">
            <section className="credit-cards-modal credit-cards-statement-modal">
              <div className="credit-cards-modal-head">
                <div>
                  <h2>{activeStatement.period} Statement</h2>
                  <p>
                    {cardById(activeStatement.cardId)?.bank} ••••{" "}
                    {cardById(activeStatement.cardId)?.last4}
                  </p>
                </div>
                <button type="button" onClick={() => setActiveStatement(null)}>
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="credit-cards-statement-section-head">
                <span>1</span>
                <h3>Statement Details</h3>
              </div>
              <div className="credit-cards-statement-summary">
                <label>
                  Statement Amount
                  <div className="credit-cards-money-input">
                    <span>RM</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      disabled={activeStatement.status !== "Pending Amount"}
                      value={activeStatement.statementAmount}
                      onClick={selectAmount}
                      onFocus={selectAmount}
                      onChange={(event) =>
                        setActiveStatement({
                          ...activeStatement,
                          statementAmount: Number(event.target.value),
                        })
                      }
                    />
                  </div>
                </label>
                <label>
                  Statement File (optional)
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    disabled={activeStatement.status !== "Pending Amount"}
                    onChange={(event) =>
                      setActiveStatement({
                        ...activeStatement,
                        fileName: event.target.files[0]?.name || "",
                      })
                    }
                  />
                  <small>
                    {activeStatement.fileName ||
                      "You may upload the monthly card statement for reference"}
                  </small>
                </label>
                <div>
                  <span>Items Total</span>
                  <strong>{money(total(activeStatement))}</strong>
                </div>
                <div>
                  <span>Difference</span>
                  <strong
                    className={
                      difference(activeStatement) === 0
                        ? "credit-cards-match"
                        : "credit-cards-mismatch"
                    }
                  >
                    {money(difference(activeStatement))}
                  </strong>
                </div>
              </div>
              <div className="credit-cards-items-head">
                <div>
                  <span>2</span>
                  <h3>Statement Items</h3>
                </div>
                <button
                  className="credit-cards-secondary"
                  disabled={activeStatement.status !== "Pending Filling"}
                  onClick={() =>
                    setActiveStatement({
                      ...activeStatement,
                      items: [
                        ...activeStatement.items,
                        {
                          id: Date.now(),
                          member:
                            cardById(activeStatement.cardId)?.members[0] || "",
                          date: "",
                          description: "",
                          amount: 0,
                        },
                      ],
                    })
                  }
                >
                  <i className="ri-add-line" /> Add Item
                </button>
              </div>
              <div className="credit-cards-item-list">
                {activeStatement.items.map((item, index) => (
                  <div className="credit-cards-item-row" key={item.id}>
                    <Select
                      classNamePrefix="credit-cards-search-select"
                      options={(cardById(activeStatement.cardId)?.members || []).map(
                        (member) => ({ value: member, label: member }),
                      )}
                      value={
                        item.member
                          ? { value: item.member, label: item.member }
                          : null
                      }
                      isDisabled={activeStatement.status !== "Pending Filling"}
                      isSearchable
                      placeholder="Select card member"
                      noOptionsMessage={() => "No matching members"}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 10000 }),
                      }}
                      onChange={(option) => {
                        const items = [...activeStatement.items];
                        items[index].member = option?.value || "";
                        setActiveStatement({ ...activeStatement, items });
                      }}
                    />
                    <input
                      type="date"
                      disabled={activeStatement.status !== "Pending Filling"}
                      value={item.date}
                      onChange={(event) => {
                        const items = [...activeStatement.items];
                        items[index].date = event.target.value;
                        setActiveStatement({ ...activeStatement, items });
                      }}
                    />
                    <input
                      disabled={activeStatement.status !== "Pending Filling"}
                      value={item.description}
                      placeholder="Expense description"
                      onChange={(event) => {
                        const items = [...activeStatement.items];
                        items[index].description = event.target.value;
                        setActiveStatement({ ...activeStatement, items });
                      }}
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      disabled={activeStatement.status !== "Pending Filling"}
                      value={item.amount}
                      onClick={selectAmount}
                      onFocus={selectAmount}
                      onChange={(event) => {
                        const items = [...activeStatement.items];
                        items[index].amount = Number(event.target.value);
                        setActiveStatement({ ...activeStatement, items });
                      }}
                    />
                    <button
                      disabled={activeStatement.status !== "Pending Filling"}
                      onClick={() =>
                        setActiveStatement({
                          ...activeStatement,
                          items: activeStatement.items.filter(
                            (row) => row.id !== item.id,
                          ),
                        })
                      }
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="credit-cards-reconcile">
                <i
                  className={
                    Number(activeStatement.statementAmount) === 0 ||
                    (difference(activeStatement) === 0 &&
                    activeStatement.items.length > 0)
                      ? "ri-checkbox-circle-fill"
                      : "ri-error-warning-fill"
                  }
                />
                <span>
                  {Number(activeStatement.statementAmount) === 0
                    ? "Zero statement amount. No expense items are required; the owner can settle and close it."
                    : difference(activeStatement) === 0 && activeStatement.items.length > 0
                      ? "Statement amount matches the entered items."
                      : "The statement amount must match the items total before owner submission."}
                </span>
              </div>
              <div className="credit-cards-modal-actions">
                <button
                  className="credit-cards-secondary"
                  onClick={saveStatement}
                >
                  Save Draft
                </button>
                {activeStatement.status === "Pending Amount" && (
                  <button
                    className="credit-cards-primary"
                    onClick={submitStatementAmount}
                  >
                    Submit Amount
                  </button>
                )}
                {activeStatement.status === "Pending Filling" && (
                  <>
                    <button
                      className="credit-cards-secondary"
                      onClick={() =>
                        changeStatus(
                          "Pending Amount",
                          "PIC reverted statement for amount correction",
                        )
                      }
                    >
                      <i className="ri-arrow-go-back-line" aria-hidden="true" />
                      Revert to Pending Amount
                    </button>
                    <button
                      className="credit-cards-primary"
                      disabled={
                        difference(activeStatement) !== 0 ||
                        Number(activeStatement.statementAmount) <= 0 ||
                        !activeStatement.items.length
                      }
                      onClick={() =>
                        changeStatus("Pending Submission", "Card owner notified")
                      }
                    >
                      Send to Card Owner
                    </button>
                  </>
                )}
                {activeStatement.status === "Pending Submission" && (
                  Number(activeStatement.statementAmount) === 0 ? (
                    <button
                      className="credit-cards-approve"
                      onClick={() => closeStatement("Zero statement settled by owner")}
                    >
                      Settle & Close
                    </button>
                  ) : (
                    <button
                      className="credit-cards-approve"
                      disabled={difference(activeStatement) !== 0 || !activeStatement.items.length}
                      onClick={createClaimFromStatement}
                    >
                      Submit for Claim
                    </button>
                  )
                )}
                {activeStatement.status === "Submitted" && (
                  <>
                    <span className="credit-cards-status submitted">
                      {activeStatement.claimId || "Submitted"}
                    </span>
                    <button
                      className="credit-cards-approve"
                      onClick={() => navigate("/claim")}
                    >
                      View in Claims
                    </button>
                  </>
                )}
                {activeStatement.status === "Closed" && (
                  <span className="credit-cards-status closed">
                    {activeStatement.claimId || "Settled without claim"}
                  </span>
                )}
              </div>
            </section>
          </div>,
          document.body,
        )}
    </main>
  );
};

export default CreditCardsPage;
